import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { finalize, tap } from 'rxjs/operators';
import { convertBase64toBlob, FRONT_CAMERA } from 'src/app/helpers/kms-lilitab.helper';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { KioskManagerService } from 'src/app/services/kiosk-manager.service';
import { LocalStorageService } from 'src/app/services/local/local-storage.service';
import { EmployeeService } from 'src/app/services/repositories/employees.service';
import { PhotoInstructionsComponent } from 'src/app/ui-widgets/modals/photo-instructions/photo-instructions.component';
import { PdModalComponent } from 'src/app/ui-widgets/modals/pd-modal/pd-modal.component';

@Component({
    selector: 'app-user-photo-uploader',
    templateUrl: './user-photo-uploader.component.html',
    styleUrls: ['./user-photo-uploader.component.scss'],
    standalone: false
})
export class UserPhotoUploaderComponent implements OnInit, AfterViewInit, AfterViewInit {
  @Input() navigateTo: string[] | null = null;
  @Output() userContinueClick = new EventEmitter<any>();

  private file: File | undefined;
  private modalSubscription: any;

  photoPreviewModalRef: NgbModalRef | undefined;
  errorsModalRef: NgbModalRef | undefined;
  instructionModalRef: NgbModalRef | undefined;
  pdModalRef: NgbModalRef | undefined;

  PROCESS_IMG_LOADER_ID = 'process-image-loader'
  isValidFile: boolean = false;
  isSubmitted: boolean = false;
  filename: string = '';
  isPhotoReady = false;
  previewImageName = '';
  employeeID: number;
  errors: any[] = [];
  isManualPD: boolean | null;
  isCameraOpen = false;
  FRONT_CAMERA = FRONT_CAMERA;

  acceptableRxFormats: string[] = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/bmp'
  ];

  isKiosk = false;

  @ViewChild('photoPreviewModal') photoPreviewModal: TemplateRef<any> | undefined;
  @ViewChild("errorsModal") errorsModal: TemplateRef<any> | undefined;

  constructor(
    private _localStorageService: LocalStorageService,
    private _employeeService: EmployeeService,
    private _authService: AuthenticationService,
    private _loaderService: NgxUiLoaderService,
    private _modalService: NgbModal,
    private _router: Router,
    private _kioskManger: KioskManagerService
  ) {
    this.employeeID = this._authService.getUserId() || 0;
    this.isManualPD = null;
  }

  ngAfterViewInit(): void {
    this.showPDSelectionModal();
  }

  ngOnInit(): void {
    this.isKiosk = this._kioskManger.isKioskMode();
  }

  private showPDSelectionModal() {
    let options: NgbModalOptions = {
      backdrop: 'static',
      keyboard: false
    };
    this.pdModalRef = this._modalService.open(PdModalComponent, options);
    this.pdModalRef.componentInstance.employeeID = this.employeeID
    this.modalSubscription = this.pdModalRef.componentInstance.onInsertNewPD
      .pipe(
        tap((size: string) => {
          (this.pdModalRef as any)._windowCmptRef.instance.size = size;
        }),
      ).subscribe();

    // On Close show the instruction
    this.pdModalRef.dismissed.subscribe(
      () => {
        this.showIntructionsModal();
        this.isManualPD = this._localStorageService.getManualPD();
      }
    );
  }

  openKioskCameraClick() {
    this.isCameraOpen = true;
  }

  showIntructionsModal() {
    this.instructionModalRef = this._modalService.open(PhotoInstructionsComponent,
      { size: 'lg', scrollable: true, backdrop: 'static' });
  }

  onFileDrop(file: File) {
    this.file = file;
    if (this.file) {
      this.validateImage();
      this.submit();
    }
  }

  validateImage() {
    if (this.file) {
      if (this.acceptableRxFormats.includes(this.file.type)) {
        this.filename = this.file.name;
        this.isValidFile = true;
      } else {
        this.isValidFile = false;
      }
    }
  }

  onFileChange(event: any) {
    if (event.target.files.length > 0) {
      this.file = event.target.files[0];
      this.validateImage();
      this.isPhotoReady = true;
    }
  }

  submit() {
    if (!this.isValidFile || !this.file) {
      return;
    }

    const data = new FormData();
    data.append('file', this.file, this.filename);

    this.uploadImage(data);
  }

  uploadImage(data: FormData) {
    if (this.isManualPD === null) {
      // TODO: Show a message that they have to select the pd type
      return;
    }

    this._loaderService.startLoader(this.PROCESS_IMG_LOADER_ID);
    this._employeeService.uploadUserImage(this.isManualPD, data)
      .pipe(finalize(() => this._loaderService.stopLoader(this.PROCESS_IMG_LOADER_ID)))
      .subscribe(
        (data) => {
          this.showProfilePicturePreviewModal(data);
        },
        (err) => {
          if (err.error.context) {
            this.showErrorModal(err.error.context);
          } else {
            this.showErrorModal([{ description: err.error.message }])
          }
        }
      );
  }

  showProfilePicturePreviewModal(data: any) {
    this.previewImageName = data.basename;
    this.photoPreviewModalRef = this._modalService.open(this.photoPreviewModal,
      { modalDialogClass: '', scrollable: true, backdrop: 'static' });
  }

  showErrorModal(errors: any[]) {
    this.errors = errors;
    this.errorsModalRef = this._modalService.open(this.errorsModal,
      { modalDialogClass: '', scrollable: true, backdrop: 'static' });
  }

  switchPhotoStatus() {
    // TODO: implement on back...
    this.isPhotoReady = false;
  }

  onCloseErrorModal() {
    this.errorsModalRef?.close();
  }

  onContinueClick() {
    this.photoPreviewModalRef?.close();
    this.userContinueClick.emit();
    if (this.navigateTo) {
      this._router.navigate(this.navigateTo);
    }
  }

  onTryAgainClick() {
    this.photoPreviewModalRef?.close();
  }


  testTimeOut() {
    console.log('CAMERA TIMED OUT');
    this.isCameraOpen = false;
  }

  onCapturedPhoto(result: any) {
    this.isCameraOpen = false;

    if (!result.data) {
      return;
    }

    var blob = convertBase64toBlob(result.data, result.contentType);
    const data = new FormData();
    data.append('file', blob, 'user_photo_from_kiosk.jpg');

    this.uploadImage(data);
  }

  ngOnDestroy(): void {
    if (this.modalSubscription) {
      this.modalSubscription.unsubscribe();
    }

    this.photoPreviewModalRef?.close();
    this.errorsModalRef?.close();
    this.instructionModalRef?.close();
    this.pdModalRef?.close();
  }
}
